import { useEffect, useMemo, useState, type CSSProperties, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { Bot, Eye, EyeOff, Layers3, PanelTopOpen, RotateCcw, Search, Square, SquareStack, Undo2, X } from 'lucide-react';
import { FloatingWindowFrame } from './FloatingWindowFrame.js';
import { useFloatingWindowStore } from '../../stores/floatingWindow.store.js';
import { useAuthStore } from '../../stores/auth.store.js';
import { projectContextStore } from '../../stores/projectContext.js';
import { pfeStore } from '../../stores/pfe.store.js';
import { ingestStore } from '../../stores/ingest.store.js';
import { useR2ChatStore } from '../../stores/r2ChatStore.js';
import { useUndoEngine } from '../../stores/undoEngine.store.js';
import { projectMemoryStore } from '../../stores/projectMemory.store.js';

const STATUS_META = {
  open: { label: 'Open', dot: '#22c55e', icon: <Eye size={12} /> },
  minimized: { label: 'Minimized', dot: '#f59e0b', icon: <EyeOff size={12} /> },
  closed: { label: 'Closed', dot: '#94a3b8', icon: <Square size={12} /> },
};

function windowStatus(win?: { open: boolean; minimized: boolean }): keyof typeof STATUS_META {
  if (win?.open && win.minimized) return 'minimized';
  if (win?.open) return 'open';
  return 'closed';
}

function managerButtonStyle(active: boolean): CSSProperties {
  return {
    height: 34,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '0 12px',
    borderRadius: 9,
    border: active ? '1px solid rgba(94,234,212,0.45)' : '1px solid rgba(148,163,184,0.16)',
    background: active ? 'rgba(94,234,212,0.13)' : 'rgba(8,14,30,0.72)',
    color: active ? '#9ffbf1' : 'rgba(226,232,240,0.74)',
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0,
  };
}

export function WindowManagerProvider(): JSX.Element {
  const setScope = useFloatingWindowStore((s) => s.setScope);
  const registerWindow = useFloatingWindowStore((s) => s.registerWindow);
  const unregisterWindow = useFloatingWindowStore((s) => s.unregisterWindow);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    const shellWindows = [
      {
        id: 'project-explorer', title: 'Project Explorer', group: 'Project', defaultRect: { x: 72, y: 80, width: 420, height: 620 }, important: true,
        onOpen: () => pfeStore.open(), onClose: () => pfeStore.close(), onMinimize: () => pfeStore.close(), onRestore: () => pfeStore.open(),
      },
      {
        id: 'smart-ingestion', title: 'Smart Ingestion', group: 'Project', defaultRect: { x: 150, y: 92, width: 760, height: 620 }, important: true,
        onOpen: () => ingestStore.open(), onClose: () => ingestStore.close(), onMinimize: () => ingestStore.close(), onRestore: () => ingestStore.open(),
      },
      {
        id: 'system-undo-history', title: 'System Undo', group: 'System', defaultRect: { x: window.innerWidth - 380, y: 88, width: 340, height: 560 }, important: true,
        onOpen: () => useUndoEngine.getState().openPanel(), onClose: () => useUndoEngine.getState().closePanel(), onMinimize: () => useUndoEngine.getState().closePanel(), onRestore: () => useUndoEngine.getState().openPanel(),
      },
      {
        id: 'r2-ai-assistant', title: 'AI Assistant', group: 'Intelligence', defaultRect: { x: window.innerWidth - 460, y: 72, width: 420, height: Math.max(520, window.innerHeight - 128) }, defaultOpen: false, important: true,
        onOpen: () => useR2ChatStore.getState().openPanel(), onClose: () => useR2ChatStore.getState().closePanel(), onMinimize: () => useR2ChatStore.setState({ isOpen: true, isMinimized: true }), onRestore: () => useR2ChatStore.getState().openPanel(),
      },
      { id: 'measurement-palette', title: 'Document Measurement', group: 'Tools', defaultRect: { x: window.innerWidth - 236, y: window.innerHeight - 320, width: 220, height: 260 }, defaultOpen: true, important: true },
      { id: 'automation-launcher', title: 'Automation Launcher', group: 'Tools', defaultRect: { x: window.innerWidth - 280, y: window.innerHeight - 360, width: 220, height: 310 }, defaultOpen: true, important: true },
    ];
    shellWindows.forEach(registerWindow);
    return () => shellWindows.forEach((w) => unregisterWindow(w.id));
  }, [registerWindow, unregisterWindow]);

  useEffect(() => {
    const sync = () => setScope(user?.email ?? user?.userId ?? 'guest', projectContextStore.getState().activeProjectId ?? 'workspace');
    sync();
    const unsub = projectContextStore.subscribe(sync);
    window.addEventListener('r2:project-active-changed', sync as EventListener);
    return () => {
      unsub();
      window.removeEventListener('r2:project-active-changed', sync as EventListener);
    };
  }, [setScope, user?.email, user?.userId]);

  useEffect(() => {
    const snapshot = projectMemoryStore.getState().snapshot;
    if (Object.keys(snapshot.openFloatingWindows).length > 0) {
      useFloatingWindowStore.getState().restoreWorkspaceState({ windows: snapshot.openFloatingWindows });
    }
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'w' || e.key === 'W')) {
        e.preventDefault();
        useFloatingWindowStore.getState().openWindow('window-manager');
      }
      if ((e.ctrlKey || e.metaKey) && e.altKey && (e.key === 'r' || e.key === 'R')) {
        e.preventDefault();
        useFloatingWindowStore.getState().quickRestore();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <WindowManagerTaskbar />
      <FloatingWindowFrame
        id="window-manager"
        title="Workspace Window Manager"
        group="Workspace Lifecycle"
        defaultRect={{ x: 96, y: 88, width: 620, height: 520 }}
        canMaximize
        keyboardClosable
        important
        accent="#5eead4"
        helpText="Open, restore, hide, bring forward, or reset any floating BIM OPS window."
      >
        <WindowManagerPanel />
      </FloatingWindowFrame>
    </>
  );
}

export function WindowManagerTaskbar(): JSX.Element | null {
  const configs = useFloatingWindowStore((s) => s.configs);
  const windows = useFloatingWindowStore((s) => s.windows);
  const openWindow = useFloatingWindowStore((s) => s.openWindow);
  const restoreWindow = useFloatingWindowStore((s) => s.restoreWindow);
  const bringToFront = useFloatingWindowStore((s) => s.bringToFront);
  const ordered = useMemo(() => Object.values(configs).filter((c) => c.id !== 'window-manager').sort((a, b) => a.title.localeCompare(b.title)), [configs]);
  if (ordered.length === 0) return null;
  const visible = ordered.slice(0, 6);
  const hiddenCount = Math.max(0, ordered.length - visible.length);

  const bar = (
    <div data-testid="workspace-window-taskbar" style={{ position: 'fixed', left: 14, right: 14, bottom: 38, zIndex: 99550, pointerEvents: 'none', display: 'flex', justifyContent: 'center' }}>
      <div style={{ pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: 6, maxWidth: 'min(980px, calc(100vw - 28px))', overflowX: 'auto', padding: '6px 8px', borderRadius: 12, background: 'rgba(3,8,18,0.82)', border: '1px solid rgba(94,234,212,0.18)', boxShadow: '0 10px 38px rgba(0,0,0,0.48)', backdropFilter: 'blur(18px)' }}>
        <button data-testid="window-manager-open-button" title="Window Manager · Ctrl+Shift+W" onClick={() => openWindow('window-manager')} style={managerButtonStyle(!!windows['window-manager']?.open)}>
          <SquareStack size={14} /> Windows{hiddenCount ? ` +${hiddenCount}` : ''}
        </button>
        {visible.map((cfg) => {
          const win = windows[cfg.id];
          const status = windowStatus(win);
          const meta = STATUS_META[status];
          return (
            <button
              key={cfg.id}
              data-testid={`window-taskbar-${cfg.id}`}
              title={`${cfg.title} · ${meta.label}`}
              onClick={() => status === 'open' ? bringToFront(cfg.id) : restoreWindow(cfg.id)}
              style={{ ...managerButtonStyle(status === 'open'), maxWidth: 172 }}
            >
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.dot, boxShadow: `0 0 7px ${meta.dot}66`, flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{cfg.title}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
  return createPortal(bar, document.body);
}

function WindowManagerPanel(): JSX.Element {
  const configs = useFloatingWindowStore((s) => s.configs);
  const windows = useFloatingWindowStore((s) => s.windows);
  const activeWindowId = useFloatingWindowStore((s) => s.activeWindowId);
  const openWindow = useFloatingWindowStore((s) => s.openWindow);
  const restoreWindow = useFloatingWindowStore((s) => s.restoreWindow);
  const bringToFront = useFloatingWindowStore((s) => s.bringToFront);
  const minimizeWindow = useFloatingWindowStore((s) => s.minimizeWindow);
  const closeWindow = useFloatingWindowStore((s) => s.closeWindow);
  const resetWindow = useFloatingWindowStore((s) => s.resetWindow);
  const closeAllFloatingWindows = useFloatingWindowStore((s) => s.closeAllFloatingWindows);
  const restorePreviousWorkspace = useFloatingWindowStore((s) => s.restorePreviousWorkspace);
  const quickRestore = useFloatingWindowStore((s) => s.quickRestore);
  const [query, setQuery] = useState('');

  const list = useMemo(() => Object.values(configs)
    .filter((c) => c.id !== 'window-manager')
    .filter((c) => `${c.title} ${c.group ?? ''}`.toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => {
      const as = windowStatus(windows[a.id]);
      const bs = windowStatus(windows[b.id]);
      const rank = { open: 0, minimized: 1, closed: 2 } as const;
      return rank[as] - rank[bs] || a.title.localeCompare(b.title);
    }), [configs, query, windows]);

  const counts = list.reduce((acc, cfg) => {
    acc[windowStatus(windows[cfg.id])] += 1;
    return acc;
  }, { open: 0, minimized: 0, closed: 0 });

  return (
    <div data-testid="window-manager-panel" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'linear-gradient(180deg, rgba(5,12,26,0.98), rgba(3,7,18,0.98))' }}>
      <div style={{ padding: 14, borderBottom: '1px solid rgba(148,163,184,0.12)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, display: 'grid', placeItems: 'center', background: 'rgba(94,234,212,0.12)', border: '1px solid rgba(94,234,212,0.28)', color: '#5eead4' }}><Layers3 size={17} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#f8fafc' }}>Workspace Window Manager</div>
            <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.62)' }}>Open, restore, hide, front, or reset every floating BIM OPS panel.</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {[['Open', counts.open, '#22c55e'], ['Minimized', counts.minimized, '#f59e0b'], ['Closed', counts.closed, '#94a3b8']].map(([label, count, color]) => (
            <div key={String(label)} data-testid={`window-manager-count-${String(label).toLowerCase()}`} style={{ padding: '5px 9px', borderRadius: 8, border: `1px solid ${color}44`, background: `${color}12`, color: String(color), fontSize: 11, fontWeight: 800 }}>{String(label)} · {String(count)}</div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: 9, color: 'rgba(148,163,184,0.48)' }} />
            <input data-testid="window-manager-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search windows…" style={{ width: '100%', height: 32, padding: '0 10px 0 30px', borderRadius: 8, border: '1px solid rgba(148,163,184,0.16)', background: 'rgba(2,6,14,0.75)', color: '#e2e8f0', fontSize: 12, outline: 'none' }} />
          </div>
          <button data-testid="window-manager-quick-restore" onClick={quickRestore} style={managerButtonStyle(false)}><Undo2 size={13} /> Quick Restore</button>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 10 }}>
        {list.map((cfg) => {
          const win = windows[cfg.id];
          const status = windowStatus(win);
          const meta = STATUS_META[status];
          return (
            <div key={cfg.id} data-testid={`window-manager-row-${cfg.id}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1fr) 96px auto', gap: 10, alignItems: 'center', padding: '10px 10px', borderRadius: 9, border: activeWindowId === cfg.id ? '1px solid rgba(94,234,212,0.38)' : '1px solid rgba(148,163,184,0.10)', background: activeWindowId === cfg.id ? 'rgba(94,234,212,0.08)' : 'rgba(8,14,30,0.46)', marginBottom: 7 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: meta.dot, boxShadow: `0 0 8px ${meta.dot}66`, flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 900, color: '#f8fafc' }}>
                    {cfg.id === 'r2-ai-assistant' && <Bot size={13} style={{ color: '#5eead4' }} />}
                    {cfg.title}
                  </div>
                  <div style={{ fontSize: 9, color: 'rgba(148,163,184,0.52)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{cfg.group ?? 'Workspace'}{win?.pinned ? ' · pinned' : ''}</div>
                </div>
              </div>
              <div data-testid={`window-manager-status-${cfg.id}`} style={{ display: 'flex', alignItems: 'center', gap: 6, color: meta.dot, fontSize: 11, fontWeight: 800 }}>{meta.icon}{meta.label}</div>
              <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button data-testid={`window-manager-open-${cfg.id}`} onClick={() => openWindow(cfg.id)} style={managerButtonStyle(false)}>Open</button>
                <button data-testid={`window-manager-restore-${cfg.id}`} onClick={() => restoreWindow(cfg.id)} style={managerButtonStyle(false)}>Restore</button>
                <button data-testid={`window-manager-front-${cfg.id}`} onClick={() => bringToFront(cfg.id)} style={managerButtonStyle(false)}>Front</button>
                <button data-testid={`window-manager-hide-${cfg.id}`} onClick={() => minimizeWindow(cfg.id)} style={managerButtonStyle(false)}>Hide</button>
                <button data-testid={`window-manager-reset-${cfg.id}`} onClick={() => resetWindow(cfg.id)} style={managerButtonStyle(false)}><RotateCcw size={12} /></button>
                <button data-testid={`window-manager-close-${cfg.id}`} onClick={() => closeWindow(cfg.id)} style={{ ...managerButtonStyle(false), color: '#fca5a5' }}><X size={12} /></button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ padding: 12, borderTop: '1px solid rgba(148,163,184,0.12)', display: 'flex', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <button data-testid="window-manager-close-all" onClick={closeAllFloatingWindows} style={{ ...managerButtonStyle(false), color: '#fca5a5' }}><X size={13} /> Close All Floating Windows</button>
        <button data-testid="window-manager-restore-workspace" onClick={restorePreviousWorkspace} style={managerButtonStyle(false)}><PanelTopOpen size={13} /> Restore Previous Workspace</button>
      </div>
    </div>
  );
}
